import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { Alert, CircularProgress } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, Suspense, useEffect, lazy } from "react";
import { AuthProvider, useAuth } from "react-oidc-context";
import { BrowserRouter, Route, Routes, useNavigate } from "react-router-dom";
import "./App.scss";
import { TopMenu } from "./components/TopMenu";
import { LocalizationProvider } from "@mui/x-date-pickers";

const AuthLoaderPage = lazy(() => import("./pages/AuthLoaderPage").then(module => ({ default: module.AuthLoaderPage })));
const Profile = lazy(() => import("./pages/Profile").then(module => ({ default: module.Profile })));
const Home = lazy(() => import("./pages/Home").then(module => ({ default: module.Home })));
const Security = lazy(() => import("./pages/Security").then(module => ({ default: module.Security })));
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { it } from "date-fns/locale";
import { withAuthenticationRequired } from "react-oidc-context";
import { useOnlineStatus } from "./hooks/use-online-status";
import { oidcConfig } from "./services/userManager";

const theme = createTheme({
  typography: {
    fontFamily: "Nunito, sans-serif",
  },
  palette: {
    primary: {
      main: "#ff002e",
    },
  },
});

const queryClient = new QueryClient();

function App() {
  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
            <AuthProvider {...oidcConfig}>
              <BrowserRouter>
                <Suspense fallback={<p>Loading</p>}>
                  <Routes>
                    <Route path="/callback" element={<Callback />}></Route>
                    <Route path="/*" element={<ProtectedInner />}></Route>
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </AuthProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </StrictMode>
  );
}
const ProtectedInner = withAuthenticationRequired(Inner, {
  OnRedirecting: () => <AuthLoaderPage />,
});
function Inner() {
  const isOnline = useOnlineStatus();

  return (
    <Suspense
      fallback={
        <CircularProgress className="absolute top-2/4 left-2/4 translate[calc(-50%-50%)]" />
      }
    >
      <TopMenu></TopMenu>
      {!isOnline && (
        <Alert severity="warning" className="mb-4">
          Sei offline. Alcune funzionalità potrebbero non essere disponibili.
        </Alert>
      )}
      <div className="container mx-auto">
        <Routes>
          {/* <Route path="/users" element={<UserList />}></Route> */}
          <Route path="/" element={<Home />}></Route>
          <Route path="/profile" element={<Profile />}></Route>
          <Route path="/security" element={<Security />}></Route>
        </Routes>
      </div>
    </Suspense>
  );
}

function Callback() {
  const { isLoading, isAuthenticated, error } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (isLoading || error) {
      return;
    }
    if (isAuthenticated) {
      navigate("/");
      return;
    }
    navigate("/logged-out");
  }, [isLoading, navigate, isAuthenticated, error]);
  if (error) {
    return <div>Errore durante il login: {error.message}</div>;
  }
  return (
    <CircularProgress className="absolute top-2/4 left-2/4 translate[calc(-50%-50%)]" />
  );
}

export default App;
